import { useMemo, useState } from 'react';
import { useGetCurrentUserQuery, useGetDegreesQuery } from '@teachur/http-client';
import { degreeTypeChoices } from '../lib/degreeChoice';

export interface DegreeChoiceOption {
  label: string;
  value: string;
}

export interface DegreeChoice {
  isLoading: boolean;
  typeOptions: DegreeChoiceOption[];
  programOptions: DegreeChoiceOption[];
  selectedType: string;
  selectedProgramId: number | null;
  hint: string | null;
  onTypeChange: (next: string) => void;
  onProgramChange: (value: string) => void;
}

export const useDegreeChoice = (initialDegreeId: number | null): DegreeChoice => {
  const { data: degrees = [], isLoading } = useGetDegreesQuery();
  const { data: user } = useGetCurrentUserQuery();
  const choices = useMemo(() => degreeTypeChoices(degrees, user), [degrees, user]);

  const initialType = degrees.find((degree) => degree.id === initialDegreeId)?.type ?? '';
  const [type, setType] = useState<string | null>(null);
  const [programId, setProgramId] = useState<number | null | undefined>(undefined);

  const selectedType = type ?? initialType;
  const choice = choices.find((c) => c.type === selectedType);
  const programs = choice?.degrees ?? [];
  const singleProgramId = programs.length === 1 ? programs[0].id : null;
  const selectedProgramId =
    programId !== undefined
      ? programId
      : type === null && programs.some((degree) => degree.id === initialDegreeId)
        ? initialDegreeId
        : singleProgramId;

  const onTypeChange = (next: string) => {
    setType(next);
    setProgramId(undefined);
  };
  const onProgramChange = (value: string) => {
    const id = Number(value);
    setProgramId(Number.isFinite(id) ? id : null);
  };

  const typeOptions = choices.map((c) => ({ label: c.label, value: c.type }));
  const programOptions = programs.map((degree) => ({
    label: degree.name,
    value: String(degree.id),
  }));

  return {
    isLoading,
    typeOptions,
    programOptions,
    selectedType,
    selectedProgramId: selectedProgramId ?? null,
    hint: choice?.blockReason ?? null,
    onTypeChange,
    onProgramChange,
  };
};
